import { ApolloClient } from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory'
import introspectionQueryResultData from './fragmentTypes.json'
import { trackGraphQLErrors, trackNetworkError } from './utils/errors'
import { browserHistory } from 'react-router'
import { loginPath } from 'lib/path-helpers'
import { toaster } from 'evergreen-v4'
import request from 'lib/request'
import { get } from 'lodash'

const httpLink = createHttpLink({
  uri: '/api/graphql',
  credentials: 'include',
  fetch: request
})

const errorHandler = onError(graphqlErrorHandler)

/*
  Add ability to retry different sort of GraphQL errors.
  While this is a really awesome idea, we won't be able to retry semantic
  GraphQL errors, but only Network errors that contain a status code, given that
  `retry-link` will only apply to > 4xx error responses.
*/
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: async (error, operation) => {
      const operationType = get(
        operation,
        'query.definitions[0].operation',
        false
      )
      const isQuery = operationType === 'query'

      // We want to make sure mutations don't get retried
      if (!isQuery) {
        return false
      }

      // We don't want to retry 401s, as they're a different class of errors
      if (error.statusCode >= 500) {
        return true
      }

      return false
    }
  }
})

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData
})

const cache = new InMemoryCache({ fragmentMatcher })

async function graphqlErrorHandler(args) {
  const { graphQLErrors, networkError, operation } = args

  if (graphQLErrors) {
    trackGraphQLErrors(graphQLErrors)
  }

  if (networkError) {
    if (networkError.statusCode === 401) {
      logoutUser()
    }

    await trackNetworkError(networkError, operation.operationName)
  }
}

function logoutUser() {
  const location = window.location.toString()
  const loggedOutPaths = [
    'login',
    'signup',
    'forgot',
    'reset',
    'enable',
    'logout'
  ]

  const isLoggedOutPage = loggedOutPaths.some(p => location.includes(p))
  if (isLoggedOutPage) {
    return
  }

  toaster.warning(
    'Your session has expired. Please re-enter your login credentials',
    10000
  )

  /*
    Resets the client so we're not longer having to deal
    with redirects because invalid cache data is available
  */
  return cache.reset().then(() => {
    const path = loginPath({
      query: {
        redirect: window.location.pathname
      }
    })

    browserHistory.push(path)
  })
}

export default new ApolloClient({
  link: errorHandler.concat(retryLink).concat(httpLink),
  cache,
  queryDeduplication: true,
  connectToDevTools: true, // Enable Apollo Devtools in production
  fragmentMatcher: new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  })
})
